import { getLocalStorageWithoutParse, removeLocalStorage, setLocalStorage } from '@/utils/local-storage';
import { useCallback } from 'react';

interface CacheOptions {
  prefix?: string;
  duration?: number;
}

interface CachedData<T> {
  data: T;
  timestamp: number;
}

export function useCache<T = any>(options?: CacheOptions) {
  const defaultOptions = {
    prefix: 'app_cache_',
    duration: 5 * 60 * 1000,
    ...options,
  };

  const getCacheKey = useCallback((key: string) => `${defaultOptions.prefix}${key}`, [defaultOptions.prefix]);

  const get = useCallback(
    (key: string): T | null => {
      try {
        const cacheKey = getCacheKey(key);
        const cached = getLocalStorageWithoutParse(cacheKey);

        if (!cached) return null;

        const { data, timestamp }: CachedData<T> = JSON.parse(cached);
        const isExpired = Date.now() - timestamp > defaultOptions.duration;

        if (isExpired) {
          removeLocalStorage(cacheKey);
          return null;
        }

        return data;
      } catch (error) {
        console.warn('Error reading from cache:', error);
        return null;
      }
    },
    [getCacheKey, defaultOptions.duration]
  );

  const set = useCallback(
    (key: string, data: T): void => {
      try {
        const cacheKey = getCacheKey(key);
        const cacheData: CachedData<T> = {
          data,
          timestamp: Date.now(),
        };

        setLocalStorage(cacheKey, JSON.stringify(cacheData));
      } catch (error) {
        console.warn('Error saving to cache:', error);
      }
    },
    [getCacheKey]
  );

  const remove = useCallback(
    (key: string): void => {
      try {
        const cacheKey = getCacheKey(key);
        removeLocalStorage(cacheKey);
      } catch (error) {
        console.warn('Error removing from cache:', error);
      }
    },
    [getCacheKey]
  );

  const clear = useCallback(
    (pattern?: string): void => {
      try {
        const keys = Object.keys(localStorage);
        const prefix = defaultOptions.prefix;

        keys.forEach((key) => {
          if (key.startsWith(prefix)) {
            if (!pattern || key.includes(pattern)) {
              localStorage.removeItem(key);
            }
          }
        });
      } catch (error) {
        console.warn('Error clearing cache:', error);
      }
    },
    [defaultOptions.prefix]
  );

  const cleanupExpired = useCallback((): void => {
    try {
      const keys = Object.keys(localStorage);
      const prefix = defaultOptions.prefix;
      let removedCount = 0;

      keys.forEach((key) => {
        if (key.startsWith(prefix)) {
          try {
            const cached = localStorage.getItem(key);
            if (cached) {
              const { timestamp }: CachedData<any> = JSON.parse(cached);
              const isExpired = Date.now() - timestamp > defaultOptions.duration;

              if (isExpired) {
                localStorage.removeItem(key);
                removedCount++;
              }
            }
          } catch (error) {
            // If we can't parse it, remove it
            localStorage.removeItem(key);
            removedCount++;
          }
        }
      });

      if (removedCount > 0) {
        console.log(`Cleaned up ${removedCount} expired cache entries`);
      }
    } catch (error) {
      console.warn('Error cleaning up expired cache:', error);
    }
  }, [defaultOptions.prefix, defaultOptions.duration]);

  const getCacheSize = useCallback((): { count: number; sizeKB: number } => {
    try {
      const keys = Object.keys(localStorage);
      const prefix = defaultOptions.prefix;
      let count = 0;
      let totalSize = 0;

      keys.forEach((key) => {
        if (key.startsWith(prefix)) {
          count++;
          const value = localStorage.getItem(key);
          if (value) {
            totalSize += key.length + value.length;
          }
        }
      });

      return {
        count,
        sizeKB: totalSize / 1024,
      };
    } catch (error) {
      console.warn('Error calculating cache size:', error);
      return { count: 0, sizeKB: 0 };
    }
  }, [defaultOptions.prefix]);

  const invalidate = useCallback(
    (key: string): void => {
      remove(key);
    },
    [remove]
  );

  const getOrSet = useCallback(
    async (key: string, fetcher: () => Promise<T>, customDuration?: number): Promise<T> => {
      const cached = get(key);
      if (cached !== null) {
        return cached;
      }

      try {
        const data = await fetcher();

        if (customDuration) {
          const originalDuration = defaultOptions.duration;
          defaultOptions.duration = customDuration;
          set(key, data);
          defaultOptions.duration = originalDuration;
        } else {
          set(key, data);
        }

        return data;
      } catch (error) {
        console.error('Error fetching data for cache:', error);
        throw error;
      }
    },
    [get, set, defaultOptions]
  );

  return {
    get,
    set,
    remove,
    clear,
    invalidate,
    getOrSet,
    cleanupExpired,
    getCacheSize,
  };
}

export function useExperienceCache() {
  return useCache({
    prefix: 'masp_tickets_',
    duration: 5 * 60 * 1000,
  });
}

export function useSeatsCache() {
  return useCache({
    prefix: 'seats_cache_',
    duration: 2 * 60 * 1000,
  });
}
